import type { Maybe } from '@adornis/base/utilTypes.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { provideContext, useContext } from '@adornis/functional-lit/directive-provide-context.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import { createContext, type Context } from '@lit/context';
import { map, of, switchMap } from 'rxjs';

export const entityContextTokenProvider = (() => {
  const map = new Map();
  return {
    getContext: <T extends typeof AdornisEntity>(entity: T) => {
      if (!map.has(entity)) map.set(entity, createContext('EntityContext' + entity._class));
      return map.get(entity) as Context<unknown, ProvideInput<T>>;
    },
  };
})();

export type ProvideInput<T extends typeof AdornisEntity> =
  | { $id: Maybe<string> }
  | { $instance: Maybe<T['prototype']> };

export function provideContextEntity<T extends typeof AdornisEntity>(entity: T, input: ProvideInput<T>) {
  const context = entityContextTokenProvider.getContext(entity);
  return provideContext(context, input);
}

export function useContextEntity<T extends typeof MongoEntity>(entity: T) {
  return <Tsel extends BaseQLSelectionSet<T['prototype']>>(selSet: Tsel) => {
    const context = entityContextTokenProvider.getContext(entity);
    const input = useContext(context);

    // always call useObservable to keep the same hook order
    const obs = () =>
      of(input).pipe(
        switchMap(input => {
          if (!input) return of(undefined);
          const instance = (() => {
            if (input.hasOwnProperty('$instance')) return (input as any).$instance;
            if (input.hasOwnProperty('$id')) return new entity({ _id: (input as any).$id });

            console.error('invalid input', { input });
            throw new Error('Invalid input');
          })();

          if (!instance) return of(undefined);
          if (instance instanceof MongoEntity) {
            const alreadyFetchedSelSet = getFetchedSelectionSet(instance);
            const allDataFetched = selectionSetAIsContainedInSelectionSetB(selSet, alreadyFetchedSelSet);
            if (allDataFetched) return instance.whenChanging;
            const completeSelectinSet = mergeSelectionSets(alreadyFetchedSelSet, selSet);
            return subscribeByID(entity, instance._id)(completeSelectinSet);
          }

          if (instance instanceof AdornisEntity) return instance.whenChanging;

          throw new Error('Invalid input: ' + JSON.stringify(input));
        }),
        map(result => [result as Maybe<NarrowedEntity<T['prototype'], Tsel>>] as const),
      );

    const entityResult = useObservable(obs, [input]);
    return entityResult ?? [undefined];
  };
}

function selectionSetAIsContainedInSelectionSetB(a: any, b: any) {
  for (const [key, value] of Object.entries(a)) {
    if (value === 1 && b[key] === 1) continue;
    if (value === 1 && b[key] !== 1) return false;
    if (value === 0) continue;
    if (typeof value === 'object' && !b[key]) return false;
    if (typeof value !== 'object' || b[key] === 1) {
      const msg = `selection set b should have a subselection set for property ${key} but has number instead`;
      console.warn(msg, { a, b });
      return false;
    }
    if (typeof value !== 'object' || typeof b[key] !== 'object') {
      console.log('first', value);
      console.log('second', b[key]);
      console.error('Invalid selection set', { a, b });
      throw new Error('Invalid selection set');
    }

    const subContain = selectionSetAIsContainedInSelectionSetB(value, b[key]);
    if (!subContain) return false;
  }

  return true;
}

function mergeSelectionSets(a: any, b: any) {
  const result = { ...a };
  for (const [key, value] of Object.entries(b)) {
    if (value === 1) result[key] = 1;
    if (typeof value === 'object') {
      if (!result[key]) result[key] = value;
      else result[key] = mergeSelectionSets(result[key], value);
    }
  }
  return result;
}

function getFetchedSelectionSet(entity: MongoEntity) {
  const fetchedSelSet = { _id: 1, _class: 1 } as any;
  for (const [key, value] of Object.entries(entity.docMeta)) {
    if (key.startsWith('_')) continue;
    if (value.omitted) continue;

    if (!(entity[key] instanceof AdornisEntity)) fetchedSelSet[key] = 1;
    else {
      fetchedSelSet[key] = getFetchedSelectionSet(entity[key] as any);
    }
  }

  return fetchedSelSet;
}
